import * as React from 'react';
import { DashboardT } from 'business/definitions';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DashboardEditMenu from 'components/DashboardEditMenu/DashboardEditMenu';
import GenericCard from 'ui/Card/GenericCard';
import { useTranslation } from 'react-i18next';
import { formatDate } from 'helpers/formatHelper';
import { getDashboardIcon } from './helpers';
import {
    usePresentationModeDashboardState,
    useFavouriteDashboardState,
} from './hooks/useDashboardState';

type Props = {
    dashboard: DashboardT;
    allowMove?: boolean;
    onClick?: () => void;
    onPropertyChange?: (
        dashboard: DashboardT,
        property: keyof DashboardT,
        value: boolean,
    ) => void;
    shouldShowFolder?: boolean;
    size?: 'sm' | 'md';
};

const DashboardCard = ({
    dashboard,
    allowMove = false,
    onClick = () => {},
    onPropertyChange = undefined,
    shouldShowFolder = false,
    size = 'sm',
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const { isInPresentationMode, togglePresentationMode } =
        usePresentationModeDashboardState(dashboard, onPropertyChange);
    const { isFavourite, toggleFavourite } = useFavouriteDashboardState(
        dashboard,
        onPropertyChange,
    );

    const lastModifiedText = dashboard.userHistory?.lastEvent
        ? t('dashboard.lastModified', {
              date: formatDate(new Date(dashboard.userHistory.lastEvent.date)),
          })
        : t('dashboard.neverModified');
    const totalUserAccessCount =
        dashboard.writeUserAccessCount + dashboard.readUserAccessCount;
    const folder =
        shouldShowFolder && dashboard.folder
            ? { name: dashboard.folder.name }
            : undefined;
    const icon = getDashboardIcon(dashboard);
    return (
        <GenericCard
            title={dashboard.title}
            icon={icon}
            route={`/dashboard/${dashboard.slug}`}
            action={
                <DashboardEditMenu
                    dashboard={dashboard}
                    show
                    small
                    allowMove={allowMove}
                    isInPresentationMode={isInPresentationMode}
                    onPresentationModeChange={togglePresentationMode}
                    isFavourite={isFavourite ?? false}
                    onFavouriteChange={toggleFavourite}
                />
            }
            onClick={onClick}
            className="pendo-dashboard-card"
            subtitle={lastModifiedText}
            chipIcon={<PersonOutlineIcon />}
            chipLabel={`${totalUserAccessCount}`}
            folder={folder}
            size={size}
        />
    );
};

export default DashboardCard;
